import { FETCH_CROPS } from '@dehaat/kisan-app-bl/api/fetchAllCropsV2';
import updateUserDetails from '@dehaat/kisan-app-bl/api/updateUserDetails';
import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common';
import Crop, { CropsWithCategoryProps } from '@dehaat/kisan-app-bl/models/CropV2';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import CropCategoryAccordion from '@/components/CropCategoryAccordion';
import ErrorModal from '@/components/ErrorModal';
import HeaderWithBackButton from '@/components/HeaderWithBackButton';
import Cross from '@/components/icons/Cross';
import LazyImage from '@/components/LazyImage';
import Okay from '@/components/modalActions/Okay';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SampleCropDisplay from '@/components/SampleCropDisplay';
import SearchBox from '@/components/SearchBox';
import SubHeading from '@/components/SubHeading';
import { ACCESS_TOKEN_KEY, REDIRECT_URI_KEY, USER_CROPS_KEY } from '@/constants/common';
import { AuthContext } from '@/context/AuthProvider';
import useDebounce from '@/hooks/useDebounce';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import styles from '@/styles/HideScrollbar.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import fetchAllCropsV2 from '@/utils/api/fetchCropsV2';
import { getLocalStorageKey, setLocalStorageKey } from '@/utils/helper';
interface Props {
  userCrops: Crop[];
  allCrops: CropsWithCategoryProps[];
}
const SelectCropsPage = ({
  userCrops,
  allCrops
}: Props) => {
  const {
    t
  } = useTranslation('crop');
  const {
    locale,
    reload,
    push,
    query
  } = useRouter();
  const cropsRef = useRef<Crop[]>([]);
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const [selectedCrops, setSelectedCrops] = useState<Crop[]>([]);
  const [filteredCrops, setFilteredCrops] = useState<Crop[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const handleRemoveCrop = (id: number) => {
    const anomUserCrops: Crop[] | undefined = getLocalStorageKey(USER_CROPS_KEY, []);
    if (anomUserCrops) {
      setLocalStorageKey(USER_CROPS_KEY, anomUserCrops.filter(crop => crop.id !== id));
    }
    setSelectedCrops(prev => prev.filter(crop => crop.id !== id));
  };
  const handleCropSelect = (crop: Crop) => {
    if (isSearching) {
      setIsSearching(false);
    }
    const isCrop = selectedCrops.some(({
      id
    }) => id === crop.id);
    if (isCrop) {
      handleRemoveCrop(crop.id);
    } else {
      setSelectedCrops([...selectedCrops, crop]);
    }
  };
  const handleSearch = useCallback((newSearchQuery: string) => {
    if (newSearchQuery) {
      const newFilteredCrops: Crop[] = [];
      const searchQueryRegex = new RegExp(newSearchQuery, 'i');
      cropsRef.current.forEach(crop => {
        if (searchQueryRegex.test(crop.name) || searchQueryRegex.test(crop.app_display_name)) {
          newFilteredCrops.push(crop);
        }
      });
      setFilteredCrops(newFilteredCrops);
    } else {
      setFilteredCrops([]);
    }
  }, []);
  const debouncedSearch = useDebounce(handleSearch, 500);
  const preventClick = selectedCrops.length === 0;
  const onContinue = async () => {
    let hasError = false;
    if (isAuthenticated) {
      const body = selectedCrops.map(crop => ({
        crop_id: crop.id
      }));
      if (!(await updateUserDetails({
        crops_details: body
      }))) {
        setErrorModalState({
          ...errorModalState,
          show: true
        });
        hasError = true;
      }
    } else {
      setLocalStorageKey(USER_CROPS_KEY, selectedCrops);
    }
    if (!hasError && !errorModalState.show) {
      const eventParams = {
        Crop_Names: selectedCrops.map(crop => crop.name).join(', '),
        Crop_IDs: selectedCrops.map(crop => crop.id).join(', ')
      };
      const eventName = query[REDIRECT_URI_KEY] === '/crop-guide' ? CustomEventTypes.CROP_GUIDE_CROP_SELECTED : CustomEventTypes.MANDI_PRICE_CROPS_ADDED;
      trackCustomEvent(eventName, JSON.stringify(eventParams));
    }
    if (query && query[REDIRECT_URI_KEY]) {
      push(`${query[REDIRECT_URI_KEY]}`);
    } else if (!hasError && !errorModalState.show) {
      reload();
    }
  };
  useEffect(() => {
    if (isSearching) document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isSearching]);
  useEffect(() => {
    let crops: Crop[] = [];
    allCrops.forEach(category => crops = category.crops.reduce((prev, crop) => crop.is_khetibook_enabled ? prev.concat(crop) : prev, crops));
    cropsRef.current = crops;
  }, [allCrops]);
  useEffect(() => {
    if (userCrops.length === 0 && !isAuthenticated) {
      const anomUserCrops = getLocalStorageKey(USER_CROPS_KEY, []);
      setSelectedCrops(anomUserCrops as Crop[]);
    }
  }, [isAuthenticated, userCrops.length]);
  useEffect(() => {
    const anomUserCrops: Crop[] | undefined = getLocalStorageKey(USER_CROPS_KEY);
    if (anomUserCrops) {
      const uniqueAnomUserCrops = anomUserCrops.filter(crop => !userCrops.find(({
        id
      }) => id === crop.id));
      setSelectedCrops([...userCrops, ...uniqueAnomUserCrops]);
    } else {
      setSelectedCrops(userCrops);
    }
  }, [userCrops]);
  useEffect(() => {
    if (query[REDIRECT_URI_KEY] === '/crop-guide') trackCustomEvent(CustomEventTypes.CROP_GUIDE_USER_LANDED_ON_CROP_SELECTION);
  }, []);
  return <main className="relative h-screen" data-sentry-component="SelectCropsPage" data-sentry-source-file="crop.tsx">
      {<HeaderWithBackButton heading={t('select_crops')} onBackClick={() => {
      query[REDIRECT_URI_KEY] === '/crop-guide' ? push('/') : undefined;
    }} />}

      <section className="w-full p-4 m-auto pt-18 max-w-7xl pb-28">
        <div className={`bg-white pt-6 ${isSearching ? 'h-screen w-full z-50' : ''}`}>
          <SearchBox lang={locale || DEFAULT_LANG} onBackClick={() => setIsSearching(false)} onFocus={() => setIsSearching(true)} onSearch={debouncedSearch} placeholder={t('type_crop_name')} showBackBtn={isSearching} data-sentry-element="SearchBox" data-sentry-source-file="crop.tsx" />

          {isSearching && filteredCrops.length > 0 && <section className="px-2 py-4" data-testid="suggestion-section">
              <Paragraph className="text-neutral-50" isHighlighted>
                {t('suggestions')}
              </Paragraph>
              {filteredCrops.map(crop => <button className="flex items-center gap-2 mt-4" key={crop.id} data-testid={`suggestion-crop-${crop.id}`} onClick={() => handleCropSelect(crop)}>
                  <div className="relative w-10 h-10 overflow-hidden border rounded-full border-neutral-40">
                    <LazyImage alt={crop.app_display_name} fallBackIcon={<div className="flex items-center justify-center w-10 h-10 overflow-hidden text-2xl rounded-full bg-neutral-300">
                          <span>{crop.app_display_name[0]}</span>
                        </div>} imageUrl={crop.image_url} className="w-10 h-10 rounded-full" />
                  </div>
                  <Paragraph className="ml-2" variant={VARIANT.BIG}>
                    {crop.app_display_name}
                  </Paragraph>
                </button>)}
            </section>}
        </div>

        <div className={`flex overflow-x-scroll gap-2 relative mt-6 ${styles.scroll}`}>
          {selectedCrops.map(({
          id,
          image_url,
          app_display_name
        }) => <div key={`selected-crop-${id}`} className="relative" data-testid={`crop-${id}`}>
              <div className="absolute top-0 right-0 z-10 w-5 h-5 p-1 rounded-full bg-primary-100" onClick={() => handleRemoveCrop(id)} data-testid="remove-selected-crop">
                <Cross className="w-3 h-3 fill-transparent stroke-white" />
              </div>
              <div className="relative overflow-hidden border rounded-full h-14 w-14 border-primary-100">
                <LazyImage alt={app_display_name} fallBackIcon={<SampleCropDisplay name={app_display_name[0]} />} imageUrl={image_url} className="rounded-full h-14 w-14" />
              </div>
            </div>)}
        </div>

        <SubHeading className="px-2 mt-3 text-neutral-80" data-sentry-element="SubHeading" data-sentry-source-file="crop.tsx">
          {t('choose_crops')}
        </SubHeading>
        {!isAuthenticated && <Paragraph className="px-2 mt-1 text-sm text-neutral-60">
            {t('please_login')}
          </Paragraph>}

        {allCrops.map(cropsWithCategory => <CropCategoryAccordion key={cropsWithCategory.category} cropCategory={{
        ...cropsWithCategory,
        crops: [...cropsWithCategory.crops].filter(crop => crop.is_khetibook_enabled)
      }} crops={selectedCrops.map(crop => crop.id)} onClick={handleCropSelect} />)}

        <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="crop.tsx">
          <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="crop.tsx" />
        </ErrorModal>
      </section>
      <footer className="fixed bottom-0 left-0 right-0 z-10 p-1 bg-white border-t border-neutral-30">
        <button onClick={onContinue} disabled={preventClick} className={`cursor-pointer py-4 m-5 ${preventClick ? 'bg-neutral-30' : 'bg-primary-100'} rounded-lg text-white text-center font-nato-semibold w-webkit-fill`} data-testid="continue-btn">
          {t('continue')}
        </button>
      </footer>
    </main>;
};
export const getServerSideProps: GetServerSideProps = async ({
  req: {
    cookies
  },
  locale
}) => {
  const accessToken = cookies[ACCESS_TOKEN_KEY];
  try {
    const [userCrops, allCrops] = await Promise.all([accessToken ? fetchAllCropsV2(locale, FETCH_CROPS.ADDED_CROPS, accessToken) : Promise.resolve([]), fetchAllCropsV2(locale, FETCH_CROPS.OTHER_THAN_ADDED_CROPS, accessToken)]);
    const selectedCrops = userCrops.length > 0 ? userCrops[0].crops.filter(crop => crop.is_khetibook_enabled) : [];
    return {
      props: {
        title: 'Select Crops',
        userCrops: selectedCrops,
        allCrops
      }
    };
  } catch {
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
};
export default SelectCropsPage;